import { FC } from 'react';

import { Link } from '@mui/material';

const URL_REGEX = /https?:\/\/[^\s]+/g;
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?)"'>]+$/;

export interface ILinkifiedSegment {
  text: string;
  isLink: boolean;
}

/**
 * Splits plain text into text/link segments. Only http(s) URLs become links;
 * trailing punctuation is kept as text so sentences like "(https://x.com)."
 * link cleanly. Rendering the segments as React text children (never HTML)
 * is what keeps untrusted content safe here.
 */
export function splitTextByUrls(text: string): ILinkifiedSegment[] {
  if (!text) return [];

  const segments: ILinkifiedSegment[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(URL_REGEX)) {
    const url = match[0].replace(TRAILING_PUNCTUATION_REGEX, '');
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({ text: text.slice(lastIndex, start), isLink: false });
    }
    segments.push({ text: url, isLink: true });
    lastIndex = start + url.length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), isLink: false });
  }
  return segments;
}

interface LinkifiedTextProps {
  text: string;
}

export const LinkifiedText: FC<LinkifiedTextProps> = ({ text }) => (
  <>
    {splitTextByUrls(text).map((segment, index) =>
      segment.isLink ? (
        <Link
          key={index}
          href={segment.text}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}
        >
          {segment.text}
        </Link>
      ) : (
        <span key={index}>{segment.text}</span>
      ),
    )}
  </>
);
